"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";

const EASE_OUT = (t: number) => 1 - Math.pow(1 - t, 3);

/**
 * Counts up every `period` ms while `running` (first tick right away); 0 means "never played".
 * `replay` plays it again now and restarts the count, so the next automatic replay is a full period away.
 */
export function useCycle(running: boolean, period: number) {
  const [cycle, setCycle] = useState(0);
  const [restart, setRestart] = useState(0);
  useEffect(() => {
    if (!running) return;
    setCycle((c) => c + 1);
    const id = window.setInterval(() => setCycle((c) => c + 1), period);
    return () => window.clearInterval(id);
  }, [running, period, restart]);
  const replay = useCallback(() => setRestart((r) => r + 1), []);
  return [cycle, replay] as const;
}

/** Tweens a number from `from` to `to` each time `cycle` changes; rests on `to`. */
function useTween(to: number, cycle: number, { from = 0, duration = 1400, delay = 0 } = {}) {
  const [value, setValue] = useState(to);
  const raf = useRef(0);
  useEffect(() => {
    if (cycle === 0) {
      setValue(to);
      return;
    }
    let start = 0;
    const step = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, Math.max(0, (now - start - delay) / duration));
      setValue(from + (to - from) * EASE_OUT(t));
      if (t < 1) raf.current = requestAnimationFrame(step);
    };
    setValue(from);
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [cycle, to, from, duration, delay]);
  return value;
}

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

/** Shared white card inside the glass (276×192). */
export function WidgetCard({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "relative flex h-[24rem] w-[34.5rem] flex-col items-start rounded-[0.5rem] border border-solid border-[rgba(0,27,20,0.08)] bg-white drop-shadow-[0_0.25rem_0.375rem_rgba(0,0,0,0.18)]",
        className,
      )}>
      {children}
    </div>
  );
}

const SEGMENT_COLORS = ["#d9e2de", "#228e71", "#003828"];

/** Card 1: the total counts up, the stacked bar grows segment by segment, the accounts count up. */
export function PointsWidget({ data, cycle }: { data: PageTradingCompetitionSchema; cycle: number }) {
  const accounts = data.rulesPointsAccounts ?? [];
  const total = accounts.reduce((sum, a) => sum + (a.points ?? 0), 0);
  const shown = useTween(total, cycle, { duration: 1600 });
  const grown = cycle > 0;

  return (
    <WidgetCard className="px-[2.25rem] pt-[2.25rem] pb-[2.5rem]">
      <p className="mb-0 font-geist text-[5.5rem] leading-none font-medium tracking-[-0.22rem] whitespace-nowrap text-[#001b14]">
        {fmt(shown)}
      </p>
      <div className="flex w-full min-h-0 flex-1 items-end gap-[0.25rem] pt-[2rem]">
        {accounts.map((a, i) => (
          <i
            key={`${a._key ?? i}-${cycle}`}
            className="h-[1rem] min-w-0 origin-left rounded-[0.125rem]"
            style={{
              flexGrow: a.points ?? 0,
              flexBasis: 0,
              background: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
              // regrows from nothing on every cycle; rests full
              animation: grown ? `tc-grow 900ms cubic-bezier(0.22,0.61,0.36,1) ${i * 180}ms both` : undefined,
            }}
          />
        ))}
      </div>
      <div className="flex w-full items-start pt-[2rem]">
        {accounts.map((a, i) => (
          <AccountColumn key={`${a._key ?? i}-${cycle}`} index={i} last={i === accounts.length - 1} label={a.label} points={a.points ?? 0} cycle={cycle} />
        ))}
      </div>
    </WidgetCard>
  );
}

function AccountColumn({ index, last, label, points, cycle }: { index: number; last: boolean; label?: string; points: number; cycle: number }) {
  const shown = useTween(points, cycle, { duration: 1200, delay: 300 + index * 180 });
  return (
    <div
      className={cn(
        "flex flex-col items-start gap-[0.75rem]",
        index === 0 ? "shrink-0 pr-[1.25rem]" : "border-l border-solid border-[rgba(0,27,20,0.08)]",
        index > 0 && !last && "shrink-0 px-[1.25rem]",
        last && index > 0 && "min-w-0 flex-1 pl-[1.25rem]",
      )}>
      <div className="flex items-center gap-[0.625rem]">
        <i className="h-[0.875rem] w-[0.875rem] shrink-0 rounded-[0.1875rem]" style={{ background: SEGMENT_COLORS[index % SEGMENT_COLORS.length] }} />
        <p className="mb-0 font-geist text-[1.25rem] leading-[normal] font-normal whitespace-nowrap text-[rgba(0,27,20,0.64)]">{label}</p>
      </div>
      <p className="mb-0 font-geist text-[2rem] leading-[normal] font-medium tracking-[-0.04rem] whitespace-nowrap text-[#001b14]">
        {fmt(shown)}
      </p>
    </div>
  );
}

/**
 * Card 2: the breach window runs out (hours count down, bars go dark from the left), then the
 * window refills in one sweep and rests full, as drawn.
 */
export function BreachWidget({ data, cycle }: { data: PageTradingCompetitionSchema; cycle: number }) {
  const hours = data.rulesBreach?.hours ?? 48;
  const left = useTween(0, cycle, { from: hours, duration: 4200, delay: 500 });
  // after draining, refill
  const [refill, setRefill] = useState(true);
  useEffect(() => {
    if (cycle === 0) return;
    setRefill(false);
    const id = window.setTimeout(() => setRefill(true), 5200);
    return () => window.clearTimeout(id);
  }, [cycle]);
  const remaining = refill ? hours : left;
  const elapsed = hours - remaining;

  return (
    <WidgetCard className="p-[2.25rem]">
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-[1rem] overflow-hidden font-geist font-medium whitespace-nowrap">
          <p className="mb-0 text-[5.5rem] leading-none tracking-[-0.22rem] text-[#001b14]">{Math.round(remaining)}h</p>
          <p className="mb-0 text-[1.375rem] leading-[normal] text-[rgba(0,27,20,0.42)]">{data.rulesBreach?.caption}</p>
        </div>
        {data.rulesBreach?.pill && (
          <div className="flex h-[2.5rem] items-center rounded-full bg-[rgba(0,27,20,0.05)] px-[1rem]">
            <p className="mb-0 font-geist text-[1.25rem] leading-[normal] font-medium whitespace-nowrap text-[rgba(0,27,20,0.64)]">
              {data.rulesBreach.pill}
            </p>
          </div>
        )}
      </div>
      <div className="flex w-full min-h-0 flex-1 flex-col items-start justify-end gap-[1rem] pt-[2rem]">
        <div className="flex h-[4.5rem] w-full items-start gap-[0.25rem] overflow-hidden">
          {Array.from({ length: hours }, (_, i) => (
            <i
              key={i}
              className="h-full min-w-0 flex-1 rounded-[0.125rem] bg-[rgba(34,142,113,0.9)] transition-[opacity,transform] ease-out"
              style={{
                opacity: i < elapsed ? 0.14 : 1,
                transform: i < elapsed ? "scaleY(0.55)" : "scaleY(1)",
                transitionDuration: refill ? "380ms" : "220ms",
                transitionDelay: refill ? `${i * 12}ms` : "0ms",
              }}
            />
          ))}
        </div>
        <div className="flex w-full items-start justify-between font-geist text-[1.1875rem] leading-[normal] font-normal whitespace-nowrap text-[rgba(0,27,20,0.42)]">
          <p className="mb-0">0h</p>
          <p className="mb-0">{Math.round(hours / 2)}h</p>
          <p className="mb-0">{hours}h</p>
        </div>
      </div>
    </WidgetCard>
  );
}

const formatMultiplier = (m?: number) => `×${m ?? 0}`;

/**
 * Card 3: the highlight walks through the account types; the headline and the chip follow it,
 * and it rests on the last (best) one, as drawn.
 */
export function MultiplierWidget({ data, cycle }: { data: PageTradingCompetitionSchema; cycle: number }) {
  const rows = data.rulesMultipliers ?? [];
  const max = Math.max(...rows.map((r) => r.multiplier ?? 0), 1);
  const lastIndex = rows.length - 1;
  const [active, setActive] = useState(lastIndex);

  useEffect(() => {
    if (cycle === 0) return;
    const timers = rows.map((_, i) => window.setTimeout(() => setActive(i), i * 1500));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle, rows.length]);

  const current = rows[active];
  return (
    <WidgetCard className="p-[2.25rem]">
      <div className="flex w-full items-start justify-between">
        <p key={`m-${active}-${cycle}`} className="tc-pop mb-0 font-geist text-[5.5rem] leading-none font-medium tracking-[-0.22rem] whitespace-nowrap text-[#001b14]">
          {formatMultiplier(current?.multiplier)}
        </p>
        <div className="flex h-[2.5rem] items-center rounded-full bg-[#e2f3ef] px-[1rem]">
          <p key={`c-${active}-${cycle}`} className="tc-pop mb-0 font-geist text-[1.25rem] leading-[normal] font-medium whitespace-nowrap text-[#0b5b45]">
            {current?.label}
          </p>
        </div>
      </div>
      <i aria-hidden="true" className="min-h-0 flex-1" />
      {rows.map((row, i) => {
        const on = i === active;
        return (
          <div
            key={row._key ?? i}
            className={cn("flex h-[4rem] w-full shrink-0 items-center gap-[1.25rem]", i > 0 && "border-t border-solid border-[rgba(0,27,20,0.08)]")}>
            <p
              className={cn(
                "mb-0 w-[10.75rem] shrink-0 font-geist text-[1.375rem] leading-[normal] font-normal transition-colors duration-300",
                on ? "text-[#001b14]" : "text-[rgba(0,27,20,0.64)]",
              )}>
              {row.label}
            </p>
            <div className="relative h-[1rem] min-w-0 flex-1 overflow-hidden rounded-[0.125rem] bg-[rgba(0,27,20,0.04)]">
              <i
                key={`b-${i}-${cycle}`}
                className="absolute top-0 left-0 h-full origin-left rounded-[0.125rem] transition-colors duration-300"
                style={{
                  // Figma: 33 / 67 / 100 px of a 98px track
                  width: `${((row.multiplier ?? 0) / max) * 102}%`,
                  background: on ? "#003828" : "#d9e2de",
                  animation: cycle > 0 ? `tc-grow 700ms cubic-bezier(0.22,0.61,0.36,1) ${i * 1500}ms both` : undefined,
                }}
              />
            </div>
            <p
              className={cn(
                "mb-0 w-[4.25rem] shrink-0 text-right font-geist text-[1.375rem] leading-[normal] font-medium transition-colors duration-300",
                on ? "text-[#001b14]" : "text-[rgba(0,27,20,0.42)]",
              )}>
              {formatMultiplier(row.multiplier)}
            </p>
          </div>
        );
      })}
    </WidgetCard>
  );
}
